import {
  FETCH_TASKS,
  FETCH_TASKS_SUCCESS,
  FETCH_TASKS_FAILED,
  ACCEPT_TASK,
  CANCEL_TASK,
} from "../store/types";
import store from "../store/store";
import { updateProfile } from "./authactions";
import { RequestPushMsg } from "../other/NotificationFunctions";
import { firebase } from '../config/configureFirebase';
import { onValue, runTransaction, get, off, push } from "firebase/database";
import { driverQueue, isDriverEligibleForBookingCar } from '../other/sharedFunctions';

const parseAmount = (value) => {
  const amount = parseFloat(value);
  return isNaN(amount) ? 0 : amount;
};

const getAdditionalFeesTotal = (booking) => {
  if (booking && booking.additionalFeesTotal !== undefined && booking.additionalFeesTotal !== null) {
    return parseAmount(booking.additionalFeesTotal);
  }
  if (booking && Array.isArray(booking.additionalFees)) {
    return booking.additionalFees.reduce((total, fee) => total + parseAmount(fee.amount), 0);
  }
  return 0;
};

const getDriverWalletThreshold = (settings, tripCost) => {
  const type = settings && settings.driverWalletThresholdType ? settings.driverWalletThresholdType : 'percentage';
  const defaultValue = type === 'percentage' ? 20 : 0;
  const thresholdValue = settings && settings.driverWalletThresholdValue !== undefined && settings.driverWalletThresholdValue !== null && settings.driverWalletThresholdValue !== ''
    ? parseAmount(settings.driverWalletThresholdValue)
    : defaultValue;
  const calculatedThreshold = type === 'flat' ? thresholdValue : (parseAmount(tripCost) * thresholdValue / 100);
  return Math.max(calculatedThreshold, parseAmount(settings && settings.driverWalletMinimumFlatRate));
};

const isFleetLinkedDriver = (profile) => !!(profile && profile.fleetadmin);

const isRequestedForDriver = (booking, uid) => !!(booking && booking.requestedDrivers && booking.requestedDrivers[uid]);

const isPublicBidTaskForFleetDriver = (booking, profile, uid) => (
  booking &&
  booking.deliveryWithBid === true &&
  isFleetLinkedDriver(profile) &&
  !isRequestedForDriver(booking, uid)
);

export const fetchTasks = () => (dispatch) => {
  const { auth, tasksRef } = firebase;

  const uid = auth.currentUser.uid;
  off(tasksRef());
  dispatch({
    type: FETCH_TASKS,
    payload: null,
  });
  onValue(tasksRef(), (snapshot) => {
    if (snapshot.val()) {
      let data = snapshot.val();
      const driverProfile = store.getState().auth && store.getState().auth.profile ? store.getState().auth.profile : {};
      const arr = Object.keys(data)
        .filter((i) => {
          const booking = data[i];
          if (!booking || booking.status !== 'NEW' || booking.driver) {
            return false;
          }
          if (booking.favoriteTranspoDriver && booking.favoriteTranspoDriver !== uid) {
            return false;
          }
          if (booking.requestedDrivers) {
            return !!booking.requestedDrivers[uid];
          }
          if (isPublicBidTaskForFleetDriver(booking, driverProfile, uid)) {
            return false;
          }
          return isDriverEligibleForBookingCar(driverProfile.carType, booking.carType);
        })
        .map((i) => {
          data[i].id = i;
          return data[i];
        });
      dispatch({
        type: FETCH_TASKS_SUCCESS,
        payload: arr,
      });
    } else {
      dispatch({
        type: FETCH_TASKS_FAILED,
        payload: store.getState().languagedata.defaultLanguage.no_tasks,
      });
    }
  });
};

export const acceptTask = (task) => (dispatch) => {
  const { auth, trackingRef, singleUserRef, singleBookingRef } = firebase;

  const uid = auth.currentUser.uid;
  const settings = store.getState().settingsdata.settings || {};

  onValue(singleUserRef(uid), (snapshot) => {
    let profile = snapshot.val();

    runTransaction(singleBookingRef(task.id),(booking) => {
      const tripCost = parseAmount(booking?.trip_cost || booking?.estimate);
      const convenienceFees = parseAmount(booking?.convenience_fees);
      const fleetCommission_fee = profile?.fleetadmin ? ((tripCost - convenienceFees) * parseAmount(booking?.fleet_admin_comission) / 100) : 0;
      const additionalFeesTotal = getAdditionalFeesTotal(booking);
      const driverAcceptanceFeeTotal = convenienceFees + fleetCommission_fee + additionalFeesTotal;
      let driver_fee = Math.max(tripCost - driverAcceptanceFeeTotal, 0).toFixed(2);
        if (booking && booking.favoriteTranspoDriver && booking.favoriteTranspoDriver !== uid) {
          return booking;
        }
        const canAcceptRequestedBooking = booking && booking.requestedDrivers
          ? !!booking.requestedDrivers[uid]
          : booking && isDriverEligibleForBookingCar(profile?.carType, booking.carType);
        if (booking && canAcceptRequestedBooking) {
          booking.driver = uid;
          booking.driver_image = profile.profile_image ? profile.profile_image : "";
          booking.car_image =  profile.car_image ? profile.car_image : "";
          booking.driver_name = profile.firstName + " " + profile.lastName;
          booking.driver_contact = profile.mobile;
          booking.driver_email = profile.email ? profile.email : "";
          booking.driver_token = profile.pushToken ? profile.pushToken : '';
          booking.vehicle_number = profile.vehicleNumber ? profile.vehicleNumber : "";
          booking.vehicleModel = profile.vehicleModel ? profile.vehicleModel : "";
          booking.vehicleMake = profile.vehicleMake ? profile.vehicleMake : "";
          booking.driverRating = profile.rating ? profile.rating : "0";
          booking.fleetCommission= fleetCommission_fee ? fleetCommission_fee.toFixed(2) : "0"
          booking.fleetadmin = profile.fleetadmin ? profile.fleetadmin : "";
          booking.status = "ACCEPTED";
          booking.driver_share = driver_fee? driver_fee : "0";
          booking.driverAcceptanceFeeTotal = driverAcceptanceFeeTotal.toFixed(2);
          booking.driverAcceptanceFeeBreakdown = {
            convenience_fees: convenienceFees.toFixed(2),
            fleetCommission: fleetCommission_fee.toFixed(2),
            additionalFeesTotal: additionalFeesTotal.toFixed(2)
          };
          booking.driverWalletThresholdRequired = getDriverWalletThreshold(settings, tripCost).toFixed(2);
          booking.driverDeviceId = task.driverDeviceId? task.driverDeviceId: null;
          booking.requestedDrivers = null;
          booking.driverEstimates = null;
          return booking;
        }
      })
      .then(() => {
        get(singleBookingRef(task.id))
          .then((snapshot) => {
            if (!snapshot.exists()) {
              return;
            } else {
              let requestedDrivers =
                snapshot.val() && snapshot.val().requestedDrivers;
              let driverId = snapshot.val() && snapshot.val().driver;

              if (requestedDrivers == undefined && driverId === uid) {
                updateProfile({ queue: driverQueue ? false : true })(dispatch);
                RequestPushMsg(task.customer_token, {
                  title:
                    store.getState().languagedata.defaultLanguage
                      .notification_title,
                  msg:
                   profile.firstName +
                    store.getState().languagedata.defaultLanguage
                      .accept_booking_request,
                  screen: "BookedCab",
                  params: { bookingId: task.id },
                });

                const driverLocation = store.getState().gpsdata.location;

                push(trackingRef(task.id), {
                  at: new Date().getTime(),
                  status: "ACCEPTED",
                  lat: driverLocation.lat,
                  lng: driverLocation.lng,
                });

                dispatch({
                  type: ACCEPT_TASK,
                  payload: { task: task },
                });
              }
            }
          })
          .catch((error) => {
            console.error(error);
          });
      });
  },{onlyOnce:true});
};

export const cancelTask = (bookingId) => (dispatch) => {
  const { auth, singleBookingRef } = firebase;

  const uid = auth.currentUser.uid;

  runTransaction(singleBookingRef(bookingId), (booking) => {
      if (booking && booking.requestedDrivers) {
        if (
          booking.requestedDrivers !== null &&
          Object.keys(booking.requestedDrivers).length === 1
        ) {
          booking.status = "NEW";
          booking.requestedDrivers = null;
          booking.driverEstimates = null;
          RequestPushMsg(booking.customer_token, {
            title:
              store.getState().languagedata.defaultLanguage.notification_title,
            msg:
              store.getState().languagedata.defaultLanguage.booking_cancelled +
              bookingId,
            screen: "BookedCab",
            params: { bookingId: bookingId },
          });
          dispatch({
            type: CANCEL_TASK,
            payload: { uid: uid, bookingId: bookingId },
          });
        }else{
          delete booking.requestedDrivers[uid];
        }
        if(booking.driverOffers && booking.driverOffers[uid]){
          delete booking.driverOffers[uid];
        }
        if(booking.selectedBid && booking.selectedBid.driver === uid){
          delete booking.selectedBid;
        }
        return booking;
      }
    });
};
